import useCarrotPlan from "#/components/carrot-plans/hooks/useCarrotPlan"
import useCompanyComplianceConfigLineItemFilter from "#/components/views/reimbursements/hooks/useGetCompanyComplianceConfigLineItemFilter"
import useBenefit, { useCreateOverride } from "#/components/dynamic-content/hooks/useGetBenefit"
import ResponseError from "#/types/responseError"
import { useGetAllDocumentStatuses } from "#/components/medical-records-consent/utils/DocumentStatusApi"
import { useDocumentStatusMapper } from "./mappers/useDocumentStatusMapper"
import { Benefit, CarrotRxPharmacyType, ProgramType } from "#/lib/carrot-api/types/Benefit"
import { useMemo } from "react"
import { CountryCode } from "#/utils/CountryCode"
import useGetCustomNetworks from "#/components/views/app/navigation/hooks/useGetCustomNetworks"
import {
  useCreateFundsAdministrationOverride,
  useGetFundsAdministration
} from "#/components/bank-details/hooks/useGetFundsAdministration"
import { MemberUserFundsAdministration } from "#/lib/carrot-api/types/MemberUserFundsAdministration"
import useGetMemberCountry from "#/components/company/hooks/useGetMemberCountry"
import useGetMemberCompany from "#/components/company/hooks/useGetMemberCompany"
import {
  useCompanyAdministrations,
  useCompanyAdministrationsOverride
} from "#/components/talk-to-carrot/hooks/useCompanyAdministration"
import { useUserInfoMapper } from "./mappers/useUserInfoMapper"
import useGetUserInfoConditionsSubset from "#/services/user/hooks/useGetCurrentUserConditionsSubset"
import { useSelector, useDispatch } from "react-redux"
import { getSubsidies } from "#/redux/reducers/subsidies"
import { getCategoryCoverage } from "#/utils/CategoryCoverage"
import { toggleSubsidiesOverride } from "#/redux/actions/testUserActions"
import { Subsidy, Subsidies, OutOfPocketMaxState, QmeState } from "#/lib/carrot-api/types/Subsidy"
import { reimbursementCategoryNameMessageKeys } from "#/components/views/reimbursements/reimbursementCategoryNameMessageKeys"
import {
  useCompanyHasCostShare,
  useCreateCompanyHasCostShareOverride
} from "#/services/user/hooks/useCompanyHasCostShare"
import { useTelemedicineForPediatricsMapper } from "#/services/contentful/hooks/mappers/useTelemedicineForPediatricsMapper"
import { useNonEnglishLocaleSelectedMapper } from "#/services/contentful/hooks/mappers/useNonEnglishLocaleSelectedMapper"
import { useCreateMobileAppStatusOverride, useMobileAppStatus } from "#/services/contentful/hooks/useMobileAppStatus"
import { useTelemedicineForPediatricsActivationStatus } from "#/components/telemedicine/pediatrics/hooks/useTelemedicineForPediatricsActivation"

const ELECTIVE_FERTILITY_LINE_ITEM = "ELECTIVE_FERTILITY"

// One-off escalation for CIBC to enable all of Carrot Academy regardless of journey support. See OP-1979 for details.
const CIBC_COMPANY_IDS = [7300, 7301, 7302]

type DependenciesStatus = {
  isLoading: boolean
  isError: boolean
  error: ResponseError | null
}

export function useConditionDepsStatus(): DependenciesStatus {
  const benefit = useBenefit()
  const memberUserFundsAdministration = useGetFundsAdministration()
  const memberUserCompany = useGetMemberCompany()
  const memberUserCountry = useGetMemberCountry()
  const carrotPlan = useCarrotPlan()
  const userInfoSubset = useGetUserInfoConditionsSubset()
  const documentStatuses = useGetAllDocumentStatuses()
  const customNetworks = useGetCustomNetworks()
  const companyComplianceConfigLineItemFilter = useCompanyComplianceConfigLineItemFilter()
  const companyAdministrations = useCompanyAdministrations()
  const companyHasCostShare = useCompanyHasCostShare()
  const telemedicineForPediatricsActivationStatus = useTelemedicineForPediatricsActivationStatus()
  const mobileAppStatus = useMobileAppStatus()
  return {
    isLoading:
      benefit.isLoading ||
      memberUserFundsAdministration?.isLoading ||
      memberUserCompany.isLoading ||
      memberUserCountry.isLoading ||
      carrotPlan.isLoading ||
      userInfoSubset.isLoading ||
      documentStatuses.isLoading ||
      customNetworks.isLoading ||
      companyComplianceConfigLineItemFilter.isLoading ||
      companyAdministrations.isLoading ||
      companyHasCostShare.isLoading ||
      telemedicineForPediatricsActivationStatus.isLoading ||
      mobileAppStatus.isLoading,
    isError:
      benefit.isError ||
      memberUserFundsAdministration?.isError ||
      memberUserCompany.isError ||
      memberUserCountry.isError ||
      carrotPlan.isError ||
      userInfoSubset.isError ||
      documentStatuses.isError ||
      customNetworks.isError ||
      companyComplianceConfigLineItemFilter.isError ||
      companyAdministrations.isError ||
      companyHasCostShare.isError ||
      telemedicineForPediatricsActivationStatus.isError ||
      mobileAppStatus.isError,
    error:
      benefit.error ||
      memberUserFundsAdministration?.error ||
      memberUserCompany.error ||
      memberUserCountry.error ||
      carrotPlan.error ||
      userInfoSubset.error ||
      documentStatuses.error ||
      customNetworks.error ||
      companyComplianceConfigLineItemFilter.error ||
      (companyAdministrations.error as ResponseError | null) ||
      companyHasCostShare.error ||
      telemedicineForPediatricsActivationStatus.error ||
      (mobileAppStatus.error as ResponseError | null)
  }
}

export function useContentfulConditions(
  allowConditionsOverride = false
): Record<string, { value: boolean; toggle: (condition?: boolean) => void }> {
  const { hasCarrotPlan } = useCarrotPlan()
  const { data } = useBenefit()
  const { data: companyComplianceData } = useCompanyComplianceConfigLineItemFilter()
  const memberUserFundsAdministration = useGetFundsAdministration()
  // Always read data so false recommendation flags do not leave this observer untracked.
  const { data: memberCountry } = useGetMemberCountry()
  const countryCode = memberCountry?.countryCode
  const memberUserCompany = useGetMemberCompany()
  const userInfoConditions = useUserInfoMapper()
  const documentStatusConditions = useDocumentStatusMapper()
  const telemedicineForPediatricsConditions = useTelemedicineForPediatricsMapper()
  const nonEnglishLocaleSelectedConditions = useNonEnglishLocaleSelectedMapper()
  const companyAdministrations = useCompanyAdministrations()
  const subsidies = useSelector(getSubsidies)
  const companyHasCostShare = useCompanyHasCostShare()
  const mobileAppStatus = useMobileAppStatus()
  const dispatch = useDispatch()
  const hasCarrotPlanCondition = {
    value: hasCarrotPlan,
    toggle: () => {
      return
    }
  }
  const companyHasCostShareCondition = {
    value: companyHasCostShare?.data,
    toggle: () => {
      const currentValue = companyHasCostShare?.data ?? false
      toggleCompanyHasCostShare(!currentValue)
    }
  }
  const companyHasDirectPayCondition = {
    value: memberUserFundsAdministration?.data?.reimbursement?.isCarrotDirectPayEnabled,
    toggle: () => {
      const currentValue = memberUserFundsAdministration?.data?.reimbursement?.isCarrotDirectPayEnabled ?? false
      const overrideData = deepFundsAdminClone(memberUserFundsAdministration?.data)
      overrideData.reimbursement.isCarrotDirectPayEnabled = !currentValue
      toggleFundsAdministrationData(overrideData)
    }
  }
  const { data: customNetworkData } = useGetCustomNetworks()
  const usesCustomFertilityProviderNetwork = {
    value: customNetworkData?.isInFertilityNetwork,
    toggle: () => {
      return
    }
  }

  const toggleBenefitData = useCreateOverride()
  const toggleFundsAdministrationData = useCreateFundsAdministrationOverride()
  const toggleComapnyAdminstrationsData = useCompanyAdministrationsOverride()
  const toggleCompanyHasCostShare = useCreateCompanyHasCostShareOverride()
  const toggleMobileAppStatus = useCreateMobileAppStatusOverride()

  const deepClone = (benefit: Benefit): Benefit => JSON.parse(JSON.stringify(benefit))
  const deepFundsAdminClone = (fundsAdministration: MemberUserFundsAdministration): MemberUserFundsAdministration =>
    JSON.parse(JSON.stringify(fundsAdministration))

  const showReadAndLearnOverride = useMemo(
    () => CIBC_COMPANY_IDS.includes(memberUserCompany?.data?.companyId),
    [memberUserCompany?.data?.companyId]
  )

  // Adding a new mapping? Check the runbook for all the usages: https://carrotfertility.atlassian.net/wiki/spaces/HIPPO/pages/2161377601/Contentful+Attribute+Mapping+Runbook
  const mapper = {
    partner_credential_access_enabled: {
      value: data?.partnerAccess?.allowPartnerCredentials,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.partnerAccess.allowPartnerCredentials = !data.partnerAccess.allowPartnerCredentials
        toggleBenefitData(overrideData)
      }
    },
    fertility_care_content: {
      value: data?.journeys.fertilityCare.isAppSupportEnabled || (allowConditionsOverride && showReadAndLearnOverride),
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.fertilityCare.isAppSupportEnabled = !data.journeys.fertilityCare.isAppSupportEnabled
        toggleBenefitData(overrideData)
      }
    },
    fertility_preservation_content: {
      value:
        data?.journeys.fertilityPreservation.isAppSupportEnabled ||
        (allowConditionsOverride && showReadAndLearnOverride),
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.fertilityPreservation.isAppSupportEnabled =
          !data.journeys.fertilityPreservation.isAppSupportEnabled
        toggleBenefitData(overrideData)
      }
    },
    pregnancy: {
      value: data?.journeys.pregnancy.isAppSupportEnabled || (allowConditionsOverride && showReadAndLearnOverride),
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.pregnancy.isAppSupportEnabled = !data.journeys.pregnancy.isAppSupportEnabled
        toggleBenefitData(overrideData)
      }
    },
    parenting: {
      value: data?.journeys.parenting.isAppSupportEnabled || (allowConditionsOverride && showReadAndLearnOverride),
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.parenting.isAppSupportEnabled = !data.journeys.parenting.isAppSupportEnabled
        toggleBenefitData(overrideData)
      }
    },
    menopause: {
      value: data?.journeys.menopause.isAppSupportEnabled || (allowConditionsOverride && showReadAndLearnOverride),
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.menopause.isAppSupportEnabled = !data.journeys.menopause.isAppSupportEnabled
        toggleBenefitData(overrideData)
      }
    },
    menopause_coverage: {
      value: data?.journeys.menopause.hasEligibleExpenses,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.menopause.hasEligibleExpenses = !data.journeys.menopause.hasEligibleExpenses
        toggleBenefitData(overrideData)
      }
    },
    low_t: {
      value:
        data?.journeys.lowTestosterone.isAppSupportEnabled || (allowConditionsOverride && showReadAndLearnOverride),
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.lowTestosterone.isAppSupportEnabled = !data.journeys.lowTestosterone.isAppSupportEnabled
        toggleBenefitData(overrideData)
      }
    },
    low_t_coverage: {
      value: data?.journeys.lowTestosterone.hasEligibleExpenses,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.lowTestosterone.hasEligibleExpenses = !data.journeys.lowTestosterone.hasEligibleExpenses
        toggleBenefitData(overrideData)
      }
    },
    show_adoption_content: {
      value: data?.journeys.adoption.isAppSupportEnabled || (allowConditionsOverride && showReadAndLearnOverride),
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.adoption.isAppSupportEnabled = !data.journeys.adoption.isAppSupportEnabled
        toggleBenefitData(overrideData)
      }
    },
    medically_necessary_preservation_rider: {
      value:
        data?.journeys.fertilityPreservation.eligibleExpensesDetail.medicallyNecessaryPreservationDetails
          .hasInsuranceCoverageForQmes,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.fertilityPreservation.eligibleExpensesDetail.medicallyNecessaryPreservationDetails.hasInsuranceCoverageForQmes =
          !data.journeys.fertilityPreservation.eligibleExpensesDetail.medicallyNecessaryPreservationDetails
            .hasInsuranceCoverageForQmes
        toggleBenefitData(overrideData)
      }
    },
    show_gestational_carrier_content: {
      value:
        data?.journeys.gestationalCarrier.isAppSupportEnabled || (allowConditionsOverride && showReadAndLearnOverride),
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.gestationalCarrier.isAppSupportEnabled =
          !data.journeys.gestationalCarrier.isAppSupportEnabled
        toggleBenefitData(overrideData)
      }
    },
    ava: {
      value: false,
      toggle: () => {
        return
      }
    },
    ivf_requirements: {
      value: data?.journeys.fertilityCare.eligibleExpensesDetail.ivfRequirementsApply,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.fertilityCare.eligibleExpensesDetail.ivfRequirementsApply =
          !data.journeys.fertilityCare.eligibleExpensesDetail.ivfRequirementsApply
        toggleBenefitData(overrideData)
      }
    },
    gestational_carrier_coverage: {
      value: data?.journeys.gestationalCarrier.hasEligibleExpenses,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.gestationalCarrier.hasEligibleExpenses =
          !data.journeys.gestationalCarrier.hasEligibleExpenses
        toggleBenefitData(overrideData)
      }
    },
    gestational_carrier_rx_covered: {
      value: data?.journeys.gestationalCarrier.eligibleExpensesDetail.isRxCovered,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.gestationalCarrier.eligibleExpensesDetail.isRxCovered =
          !data.journeys.gestationalCarrier.eligibleExpensesDetail.isRxCovered
        toggleBenefitData(overrideData)
      }
    },
    elective_art: {
      value: data?.journeys.fertilityCare.eligibleExpensesDetail.isElectiveCareCovered,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.fertilityCare.eligibleExpensesDetail.isElectiveCareCovered =
          !data.journeys.fertilityCare.eligibleExpensesDetail.isElectiveCareCovered
        toggleBenefitData(overrideData)
      }
    },
    infertility_diagnosis: {
      value: data?.journeys.fertilityCare.eligibleExpensesDetail.isCoveredWithInfertilityDiagnosis,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.fertilityCare.eligibleExpensesDetail.isCoveredWithInfertilityDiagnosis =
          !data.journeys.fertilityCare.eligibleExpensesDetail.isCoveredWithInfertilityDiagnosis
        toggleBenefitData(overrideData)
      }
    },
    medically_necessary_preservation: {
      value: data?.journeys.fertilityPreservation.eligibleExpensesDetail.isMedicallyNecessaryPreservationCovered,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.fertilityPreservation.eligibleExpensesDetail.isMedicallyNecessaryPreservationCovered =
          !data.journeys.fertilityPreservation.eligibleExpensesDetail.isMedicallyNecessaryPreservationCovered
        toggleBenefitData(overrideData)
      }
    },
    elective_preservation: {
      value: data?.journeys.fertilityPreservation.eligibleExpensesDetail.isElectivePreservationCovered,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.fertilityPreservation.eligibleExpensesDetail.isElectivePreservationCovered =
          !data.journeys.fertilityPreservation.eligibleExpensesDetail.isElectivePreservationCovered
        toggleBenefitData(overrideData)
      }
    },
    adoption_coverage: {
      value: data?.journeys.adoption.hasEligibleExpenses,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.adoption.hasEligibleExpenses = !data.journeys.adoption.hasEligibleExpenses
        toggleBenefitData(overrideData)
      }
    },
    preservation: {
      value: data?.journeys.fertilityPreservation.hasEligibleExpenses,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.fertilityPreservation.hasEligibleExpenses =
          !data.journeys.fertilityPreservation.hasEligibleExpenses
        toggleBenefitData(overrideData)
      }
    },
    donor_assistance: {
      value: data?.journeys.fertilityCare.eligibleExpensesDetail.hasDonorAssistance,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.fertilityCare.eligibleExpensesDetail.hasDonorAssistance =
          !data.journeys.fertilityCare.eligibleExpensesDetail.hasDonorAssistance
        toggleBenefitData(overrideData)
      }
    },
    doula_expenses_covered: {
      value: data?.journeys.pregnancy.eligibleExpensesDetail.doulaExpensesCovered,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.pregnancy.eligibleExpensesDetail.doulaExpensesCovered =
          !data.journeys.pregnancy.eligibleExpensesDetail.doulaExpensesCovered
        toggleBenefitData(overrideData)
      }
    },
    childbirth_classes_covered: {
      value: data?.journeys.pregnancy.eligibleExpensesDetail.childbirthClassesCovered,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.pregnancy.eligibleExpensesDetail.childbirthClassesCovered =
          !data.journeys.pregnancy.eligibleExpensesDetail.childbirthClassesCovered
        toggleBenefitData(overrideData)
      }
    },
    milk_shipping_covered: {
      value: data?.journeys.pregnancy.eligibleExpensesDetail.milkShippingCovered,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.pregnancy.eligibleExpensesDetail.milkShippingCovered =
          !data.journeys.pregnancy.eligibleExpensesDetail.milkShippingCovered
        toggleBenefitData(overrideData)
      }
    },
    mira: {
      value: data?.journeys.fertilityCare.isMiraEnabled,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.fertilityCare.isMiraEnabled = !data.journeys.fertilityCare.isMiraEnabled
        toggleBenefitData(overrideData)
      }
    },
    carrot_rx: {
      value: data?.carrotRx.isCarrotRxActive,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.carrotRx.isCarrotRxActive = !data.carrotRx.isCarrotRxActive
        toggleBenefitData(overrideData)
      }
    },
    carrot_rx_required: {
      value: data?.carrotRx.isCarrotRxRequired,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.carrotRx.isCarrotRxRequired = !data.carrotRx.isCarrotRxRequired
        toggleBenefitData(overrideData)
      }
    },
    fertility_care_rx_covered: {
      value: data?.journeys.fertilityCare.eligibleExpensesDetail.isRxCovered,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.fertilityCare.eligibleExpensesDetail.isRxCovered =
          !data.journeys.fertilityCare.eligibleExpensesDetail.isRxCovered
        toggleBenefitData(overrideData)
      }
    },
    fertility_preservation_rx_covered: {
      value: data?.journeys.fertilityPreservation.eligibleExpensesDetail.isRxCovered,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.fertilityPreservation.eligibleExpensesDetail.isRxCovered =
          !data.journeys.fertilityPreservation.eligibleExpensesDetail.isRxCovered
        toggleBenefitData(overrideData)
      }
    },
    obgyn_provided_fertility_care_covered: {
      value: data?.journeys.fertilityCare.eligibleExpensesDetail.obGynProvidedFertilityCareCovered,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.fertilityCare.eligibleExpensesDetail.obGynProvidedFertilityCareCovered =
          !data.journeys.fertilityCare.eligibleExpensesDetail.obGynProvidedFertilityCareCovered
        toggleBenefitData(overrideData)
      }
    },
    custom_coverage: {
      value: !!data?.customizations.memberCustomCoverageExplanationMarkdown,
      toggle: () => {
        const overrideData = deepClone(data)
        if (overrideData?.customizations.memberCustomCoverageExplanationMarkdown) {
          overrideData.customizations.memberCustomCoverageExplanationMarkdown = null
        } else {
          overrideData.customizations.memberCustomCoverageExplanationMarkdown =
            "This is a test string. If you were expecting to see the original Custom Coverage text, please refresh the page."
        }
        toggleBenefitData(overrideData)
      }
    },
    additional_employer_benefits: {
      value: !!data?.customizations.additionalEmployerBenefitMarkdown,
      toggle: () => {
        const overrideData = deepClone(data)
        if (overrideData.customizations.additionalEmployerBenefitMarkdown) {
          overrideData.customizations.additionalEmployerBenefitMarkdown = null
        } else {
          overrideData.customizations.additionalEmployerBenefitMarkdown =
            "This is a test string. If you were expecting to see the original Additional Employer Benefit text, please refresh the page."
        }
        toggleBenefitData(overrideData)
      }
    },
    health_plan_custom_info: {
      value: !!data?.customizations.healthPlanMarkdown,
      toggle: () => {
        const overrideData = deepClone(data)
        if (overrideData.customizations.healthPlanMarkdown) {
          overrideData.customizations.healthPlanMarkdown = null
        } else {
          overrideData.customizations.healthPlanMarkdown =
            "This is a test string. If you were expecting to see the original Health Plan text, please refresh the page."
        }
        toggleBenefitData(overrideData)
      }
    },
    provider_finder_show_gender_affirming_hormone_therapy: {
      value: data?.providerFinder.showGenderAffirmingHormoneTherapy,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.providerFinder.showGenderAffirmingHormoneTherapy =
          !data.providerFinder.showGenderAffirmingHormoneTherapy
        toggleBenefitData(overrideData)
      }
    },
    show_provider_finder: {
      value: data?.providerFinder.showProviderFinder,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.providerFinder.showProviderFinder = !data.providerFinder.showProviderFinder
        toggleBenefitData(overrideData)
      }
    },
    carrot_pro: {
      value: data?.program.type === ProgramType.PRO,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.program.type = data.program.type === ProgramType.PRO ? ProgramType.CORE : ProgramType.PRO
        overrideData.program.isCarrotLite = overrideData.program.type !== ProgramType.PRO
        toggleBenefitData(overrideData)
      }
    },
    carrot_core: {
      value: data?.program.type === ProgramType.CORE,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.program.type = data.program.type === ProgramType.CORE ? ProgramType.PRO : ProgramType.CORE
        overrideData.program.isCarrotLite = overrideData.program.type !== ProgramType.PRO
        toggleBenefitData(overrideData)
      }
    },
    wtw_engage: {
      value: data?.program.type === ProgramType.WTW_ENGAGE,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.program.type =
          data.program.type === ProgramType.WTW_ENGAGE ? ProgramType.PRO : ProgramType.WTW_ENGAGE
        overrideData.program.isCarrotLite = overrideData.program.type !== ProgramType.PRO
        toggleBenefitData(overrideData)
      }
    },
    consumer: {
      value: data?.program.type === ProgramType.CONSUMER,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.program.type = data.program.type === ProgramType.CONSUMER ? ProgramType.PRO : ProgramType.CONSUMER
        overrideData.program.isCarrotLite = overrideData.program.type !== ProgramType.PRO
        toggleBenefitData(overrideData)
      }
    },
    gender_affirming_care: {
      value:
        data?.journeys.genderAffirmingCare.isAppSupportEnabled || (allowConditionsOverride && showReadAndLearnOverride),
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.genderAffirmingCare.isAppSupportEnabled =
          !data.journeys.genderAffirmingCare.isAppSupportEnabled
        toggleBenefitData(overrideData)
      }
    },
    gender_affirming_care_coverage: {
      value: data?.journeys.genderAffirmingCare.hasEligibleExpenses,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.genderAffirmingCare.hasEligibleExpenses =
          !data.journeys.genderAffirmingCare.hasEligibleExpenses
        toggleBenefitData(overrideData)
      }
    },
    travel_for_reproductive_care: {
      value: data?.journeys.fertilityCare.eligibleExpensesDetail.areTravelCostsForTerminationCovered,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.fertilityCare.eligibleExpensesDetail.areTravelCostsForTerminationCovered =
          !data.journeys.fertilityCare.eligibleExpensesDetail.areTravelCostsForTerminationCovered
        toggleBenefitData(overrideData)
      }
    },
    elective_fertility_qme_line_item_filter: {
      value: companyComplianceData === ELECTIVE_FERTILITY_LINE_ITEM,
      // @ts-expect-error TS(7011) FIXME: Function expression, which lacks return-type annot... Remove this comment to see the full error message
      toggle: () => null
    },
    fertility_care_coverage: {
      value: data?.journeys.fertilityCare.hasEligibleExpenses,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.journeys.fertilityCare.hasEligibleExpenses = !data.journeys.fertilityCare.hasEligibleExpenses
        toggleBenefitData(overrideData)
      }
    },
    show_donor_content: {
      value: data?.generalSupport.showDonorContent,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.generalSupport.showDonorContent = !data.generalSupport.showDonorContent
        toggleBenefitData(overrideData)
      }
    },
    show_lgbtq_content: {
      value: data?.generalSupport.showLgbtqContent,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.generalSupport.showLgbtqContent = !data.generalSupport.showLgbtqContent
        toggleBenefitData(overrideData)
      }
    },
    show_fifc_content: {
      value: data?.generalSupport.faithInformedCareEnabled,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.generalSupport.faithInformedCareEnabled = !data.generalSupport.faithInformedCareEnabled
        toggleBenefitData(overrideData)
      }
    },
    provider_finder_show_donor_assistance: {
      value: data?.providerFinder.showDonorAssistance,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.providerFinder.showDonorAssistance = !data.providerFinder.showDonorAssistance
        toggleBenefitData(overrideData)
      }
    },
    provider_finder_show_adoption: {
      value: data?.providerFinder.showAdoption,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.providerFinder.showAdoption = !data.providerFinder.showAdoption
        toggleBenefitData(overrideData)
      }
    },
    provider_finder_show_gestational_surrogacy: {
      value: data?.providerFinder.showGestationalSurrogacy,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.providerFinder.showGestationalSurrogacy = !data.providerFinder.showGestationalSurrogacy
        toggleBenefitData(overrideData)
      }
    },
    provider_finder_show_low_t: {
      value: data?.providerFinder.showLowTestosterone,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.providerFinder.showLowTestosterone = !data.providerFinder.showLowTestosterone
        toggleBenefitData(overrideData)
      }
    },
    provider_finder_show_menopause: {
      value: data?.providerFinder.showMenopause,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.providerFinder.showMenopause = !data.providerFinder.showMenopause
        toggleBenefitData(overrideData)
      }
    },
    provider_finder_show_obgyn_recommendations: {
      value: data?.providerFinder.showObGynRecommendations && countryCode === CountryCode.United_States,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.providerFinder.showObGynRecommendations =
          !data.providerFinder.showObGynRecommendations && countryCode === CountryCode.United_States
        toggleBenefitData(overrideData)
      }
    },
    provider_finder_show_pediatrician_recommendations: {
      value: data?.providerFinder.showPediatricianRecommendations && countryCode === CountryCode.United_States,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.providerFinder.showPediatricianRecommendations =
          !data.providerFinder.showPediatricianRecommendations && countryCode === CountryCode.United_States
        toggleBenefitData(overrideData)
      }
    },
    provider_finder_show_fertility_clinics: {
      value: data?.providerFinder.showFertilityClinics,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.providerFinder.showFertilityClinics = !data.providerFinder.showFertilityClinics
        toggleBenefitData(overrideData)
      }
    },
    provider_finder_show_reproductive_urology: {
      value: data?.providerFinder.showReproductiveUrology,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.providerFinder.showReproductiveUrology = !data.providerFinder.showReproductiveUrology
        toggleBenefitData(overrideData)
      }
    },
    // Controls whether doulas are shown in the Provider Finder - mapped to member app and admin v2
    // See OP-2719 for implementation details
    provider_finder_show_doulas: {
      value: data?.providerFinder.showDoulas,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.providerFinder.showDoulas = !data.providerFinder.showDoulas
        toggleBenefitData(overrideData)
      }
    },
    provider_finder_show_cryobanks_storage: {
      value: data?.providerFinder.showCryobanks,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.providerFinder.showCryobanks = !data.providerFinder.showCryobanks
        toggleBenefitData(overrideData)
      }
    },
    provider_finder_show_pelvic_floor_therapy: {
      value: data?.providerFinder.showPelvicFloorTherapy,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.providerFinder.showPelvicFloorTherapy = !data.providerFinder.showPelvicFloorTherapy
        toggleBenefitData(overrideData)
      }
    },
    wheel_telemedicine_for_menopause: {
      value: data?.telemedicine?.providers?.wheelForMenopauseEnabled,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.telemedicine.providers.wheelForMenopauseEnabled =
          !data.telemedicine.providers.wheelForMenopauseEnabled
        toggleBenefitData(overrideData)
      }
    },
    blueberry_telemedicine_for_pediatrics: {
      value: data?.telemedicine?.providers?.blueberryPediatricsEnabled,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.telemedicine.providers.blueberryPediatricsEnabled =
          !data.telemedicine.providers.blueberryPediatricsEnabled
        toggleBenefitData(overrideData)
      }
    },
    pelvic_floor_therapy: {
      value: data?.pelvicFloorTherapyTelemedicine?.enabled,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.pelvicFloorTherapyTelemedicine.enabled = !data.pelvicFloorTherapyTelemedicine.enabled
        toggleBenefitData(overrideData)
      }
    },
    pelvic_floor_therapy_covered: {
      value: data?.pelvicFloorTherapyTelemedicine?.hasEligibleExpenses,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.pelvicFloorTherapyTelemedicine.hasEligibleExpenses =
          !data.pelvicFloorTherapyTelemedicine.hasEligibleExpenses
        toggleBenefitData(overrideData)
      }
    },
    custom_reimbursement_guidance: {
      value: false,
      toggle: () => {}
    },
    legal_partners_eligible: {
      value: data?.partnerEligibility?.limitations?.mustBeLegalPartner,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.partnerEligibility.limitations.mustBeLegalPartner =
          !data.partnerEligibility?.limitations?.mustBeLegalPartner
        toggleBenefitData(overrideData)
      }
    },
    member_and_partner_eligible: {
      value: data?.partnerEligibility?.isEligibleForCoverage,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.partnerEligibility.isEligibleForCoverage = !data.partnerEligibility?.isEligibleForCoverage
        toggleBenefitData(overrideData)
      }
    },
    partner_eligible_if_on_customer_medical_plan: {
      value: data?.partnerEligibility?.limitations?.mustBeOnCompanyMedicalPlan,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.partnerEligibility.limitations.mustBeOnCompanyMedicalPlan =
          !data.partnerEligibility?.limitations?.mustBeOnCompanyMedicalPlan
        toggleBenefitData(overrideData)
      }
    },
    bank_info: {
      value: memberUserFundsAdministration?.data?.isBankInfoRequired,
      toggle: () => {
        const overrideData = deepFundsAdminClone(memberUserFundsAdministration?.data)
        overrideData.isBankInfoRequired = !memberUserFundsAdministration?.data?.isBankInfoRequired
        toggleFundsAdministrationData(overrideData)
      }
    },
    gross_up: {
      value: memberUserFundsAdministration?.data?.isGrossUpEnabled,
      toggle: () => {
        const overrideData = deepFundsAdminClone(memberUserFundsAdministration?.data)
        overrideData.isGrossUpEnabled = !memberUserFundsAdministration?.data?.isGrossUpEnabled
        toggleFundsAdministrationData(overrideData)
      }
    },
    copay: {
      value: memberUserFundsAdministration?.data?.isMedicalCopayCoverageEnabled,
      toggle: () => {
        const overrideData = deepFundsAdminClone(memberUserFundsAdministration?.data)
        overrideData.isMedicalCopayCoverageEnabled = !memberUserFundsAdministration?.data?.isMedicalCopayCoverageEnabled
        toggleFundsAdministrationData(overrideData)
      }
    },
    us_carrots_only: {
      value: companyAdministrations?.data?.usCarrotsOnly,
      toggle: () => {
        const overrideData = JSON.parse(JSON.stringify(memberUserFundsAdministration?.data))
        overrideData.usCarrotsOnly = !companyAdministrations?.data?.usCarrotsOnly
        toggleComapnyAdminstrationsData(overrideData)
      }
    },
    freedom_fertilty_carrot_rx: {
      value: data?.carrotRx?.fertilityPharmacy === CarrotRxPharmacyType.FREEDOM_FERTILITY,
      toggle: () => {
        const overrideData = deepClone(data)
        overrideData.carrotRx.fertilityPharmacy =
          data?.carrotRx?.fertilityPharmacy === CarrotRxPharmacyType.FREEDOM_FERTILITY
            ? CarrotRxPharmacyType.ALTO
            : CarrotRxPharmacyType.FREEDOM_FERTILITY
        toggleBenefitData(overrideData)
      }
    },
    has_fund_category_for_qualified_medical_expenses: {
      value: getCategoryCoverage(subsidies).qme ?? false,
      toggle: () => {
        const currentSubsidies = subsidies || []
        const hasQmeSubsidy = currentSubsidies.some(
          (subsidy: Subsidy) => subsidy.reimbursementCategoryNameMessageKey === reimbursementCategoryNameMessageKeys.QME
        )

        let updatedSubsidies: Subsidies

        if (hasQmeSubsidy) {
          // Remove QME subsidy if it exists
          updatedSubsidies = currentSubsidies.filter(
            (subsidy: Subsidy) =>
              subsidy.reimbursementCategoryNameMessageKey !== reimbursementCategoryNameMessageKeys.QME
          )
        } else {
          // Add QME subsidy if it doesn't exist
          const qmeSubsidy: Subsidy = {
            planStarted: true,
            planStartDate: "2021-01-01",
            yearlyAmounts: {
              max: 2000000,
              available: 2000000,
              processing: 0,
              used: 0,
              claimed: 0
            },
            lifetimeAmounts: {
              max: 10000000,
              available: 10000000,
              processing: 0,
              used: 0,
              claimed: 0
            },
            reimbursementRules: [
              { reimbursementPercent: 50.0, outOfPocketMaxState: OutOfPocketMaxState.ANY, qmeState: QmeState.ANY }
            ],
            hasUnlimitedBenefit: false,
            reimbursementCategoryNameMessageKey: reimbursementCategoryNameMessageKeys.QME,
            hasCostShare: true
          }
          updatedSubsidies = [...currentSubsidies, qmeSubsidy]
        }

        // Update the subsidies data using Redux action
        dispatch(toggleSubsidiesOverride(updatedSubsidies))
      }
    },
    ios_app: {
      value: mobileAppStatus?.data?.isIOS,
      toggle: () => {
        toggleMobileAppStatus({ ...mobileAppStatus?.data, isIOS: !mobileAppStatus?.data?.isIOS })
      }
    },
    android_app: {
      value: mobileAppStatus?.data?.isAndroid,
      toggle: () => {
        toggleMobileAppStatus({ ...mobileAppStatus?.data, isAndroid: !mobileAppStatus?.data?.isAndroid })
      }
    }
  }
  return {
    has_carrot_plan: hasCarrotPlanCondition,
    uses_custom_fertility_provider_network: usesCustomFertilityProviderNetwork,
    has_cost_share: companyHasCostShareCondition,
    has_direct_pay: companyHasDirectPayCondition,
    ...userInfoConditions,
    ...documentStatusConditions,
    ...telemedicineForPediatricsConditions,
    ...nonEnglishLocaleSelectedConditions,
    ...mapper
  }
}
